import { getLocalizedText } from "./i18n/i18n.utils";
// ── Configurable weights — change these to rebalance scoring ──────────
export var WEIGHTS = {
    income: 50,
    household: 40,
    date: 10,
};
// ── A. Income helpers ─────────────────────────────────────────────────
function getIncomeThresholds(rent, adults, minors) {
    var rentFactor = Math.max(0, Math.min(1, (rent - 500) / 2500));
    var perfectRatio = 3.0 - 1.0 * rentFactor; // 3.0× at €500 → 2.0× at €3000+
    var minRatio = 2.0 - 0.5 * rentFactor; // 2.0× at €500 → 1.5× at €3000+
    // Large family income penalty
    var totalPeople = adults + minors;
    if (totalPeople > 2) {
        var penalty = (totalPeople - 2) * 0.1;
        minRatio += penalty;
        perfectRatio += penalty;
    }
    return { perfectRatio: perfectRatio, minRatio: minRatio };
}
function calcIncomePct(user, property) {
    var totalIncome = user.netIncome + user.otherIncome;
    if (totalIncome <= 0)
        return 0;
    var ratio = totalIncome / property.rent;
    var _a = getIncomeThresholds(property.rent, user.adults, user.minors), perfectRatio = _a.perfectRatio, minRatio = _a.minRatio;
    if (ratio >= perfectRatio)
        return 1.0;
    if (ratio <= minRatio)
        return 0;
    return (ratio - minRatio) / (perfectRatio - minRatio);
}
// ── B. Household helpers ──────────────────────────────────────────────
function calcHouseholdPct(user, property, incomePct) {
    if (property.bedrooms === null)
        return 1.0;
    // First 2 adults share 1 room (couple), extra adults each need a room
    // Children: 0.75 rooms per child (≈ 2 children per 1.5 rooms)
    var roomsNeeded = 1 + Math.max(0, user.adults - 2) + user.minors * 0.75;
    var excess = Math.max(0, roomsNeeded - property.bedrooms);
    // Can go negative — penalties eat into other categories' scores
    var pct = 1 - excess;
    if (property.bedrooms > Math.ceil(roomsNeeded) && incomePct < 1.0) {
        pct = Math.min(pct, 0.9);
    }
    return pct;
}
// ── C. Date helpers ───────────────────────────────────────────────────
//
// The date comparison below exists three times and the three must agree,
// otherwise the same candidate gets one score in the mail, another one on the
// calculator and a third one in this dashboard:
//   1. rentmatch/src/rentmatch/utils/scoreCalculator.ts        (calculator + candidate match list)
//   2. rentio-api-serverless/src/domains/tenantProfile/worker/
//        rentMatchNotification/helper/scoreCalculator.ts       (digest mail)
//   3. this file                                               (broker dashboard)
// They are kept separate on purpose (three repos, three release trains, and
// this copy carries dashboard-only extras), so changing the rules here means
// changing them in the other two as well. The two copies that have a test
// runner share a table of worked-out cases named SHARED_DATE_CASES; keep that
// table in step with any change made here.
var DAY_IN_MS = 86400000;
// Whole calendar days between two dates, counted on the UTC calendar.
//
// Subtracting two local midnights in milliseconds is off by an hour whenever a
// daylight-saving switch falls inside the interval, which was enough to push an
// exactly-30-day gap just outside the 30 day tolerance twice a year. Mapping
// each date to a day number first keeps the comparison on whole days.
var toDayIndex = function (date) {
    return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_IN_MS);
};
// Both dates are pulled forward to today before they are compared.
//
// A property standing empty is available *from today*, not from the date in
// the past when it first became free. Comparing against that past date
// punished exactly the properties that suit someone who wants to move
// immediately: a flat empty for a year scored 1 out of 100 for a candidate who
// could move in tomorrow. It also measured how long the property had already
// been vacant, which the candidate cannot influence.
//
// The same holds for the candidate's own date: a desired move-in date in the
// past means "as soon as possible". Left as it was, it was the shortest route
// to a perfect score.
//
// Wanting to move later than the property is free stays a penalty on purpose:
// a candidate who can move sooner will be preferred, so the lower score is
// honest. The penalty now measures how many extra empty weeks the landlord
// faces because of this candidate.
//
// "ASAP" tolerates a month, a named date tolerates three days. Both branches
// reach this helper for the ASAP case so those two tolerances cannot drift
// apart again: a candidate whose named date has already passed means ASAP, and
// scoring them on the three-day tolerance cost them 15 points against an
// identical candidate whose intent was read as ASAP.
var scoreAsSoonAsPossible = function (availableFrom, todayIndex) {
    var daysApart = availableFrom - todayIndex;
    if (daysApart <= 30)
        return 1.0;
    // Not clamped at 0 on purpose: a date far out of reach has to be able to
    // disqualify, otherwise it could never cost more than its own 10 points.
    return 1 - (daysApart - 30) / 20;
};
function calcDatePct(user, property) {
    if (!property.availableDate)
        return 1.0;
    if (user.moveInDateType === "flexible")
        return 1.0;
    if (user.moveInDateType === "" || user.moveInDateType === undefined)
        return 0; // not filled → 0 pts
    var availDate = new Date(property.availableDate);
    // A value that cannot be read as a date (e.g. the MySQL zero date
    // "0000-00-00") is not a real constraint, so treat it like a missing date
    // instead of producing NaN scores. Same guard as rentmatch's own copy.
    if (Number.isNaN(availDate.getTime()))
        return 1.0;
    var todayIndex = toDayIndex(new Date());
    var availableFrom = Math.max(toDayIndex(availDate), todayIndex);
    if (user.moveInDateType === "immediately") {
        return scoreAsSoonAsPossible(availableFrom, todayIndex);
    }
    if (user.moveInDateType === "specific" && user.specificMoveInDate) {
        var specificDate = new Date(user.specificMoveInDate);
        if (Number.isNaN(specificDate.getTime()))
            return 0;
        var wantedIndex = toDayIndex(specificDate);
        // A named date that has already passed says the same thing as ASAP, so it
        // is scored the same way rather than on the tighter tolerance a
        // deliberately chosen future date gets. A candidate whose occupancy date
        // sits in the past means "as soon as you can have me", not "on that exact
        // day".
        if (wantedIndex <= todayIndex) {
            return scoreAsSoonAsPossible(availableFrom, todayIndex);
        }
        var daysApart = Math.abs(availableFrom - wantedIndex);
        if (daysApart <= 3) {
            return 1.0;
        }
        // Same penalty for too early AND too late, measured from today.
        return 1 - daysApart / 20;
    }
    return 0;
}
// ── Warnings ──────────────────────────────────────────────────────────
function calcWarnings(user, property, incomePct, householdPct, datePct) {
    var warnings = [];
    var totalIncome = user.netIncome + user.otherIncome;
    // Income warnings
    if (totalIncome > 0) {
        if (incomePct >= 0.8 && incomePct < 1.0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_slightly_below"),
            });
        }
        else if (incomePct >= 0.5 && incomePct < 0.8) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_below"),
            });
        }
        else if (incomePct > 0 && incomePct < 0.5) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.income_significantly_below"),
            });
        }
        else if (incomePct === 0 && totalIncome > 0) {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.income_too_low"),
            });
        }
    }
    // Household warnings
    var roomsNeeded = 1 + Math.max(0, user.adults - 2) + user.minors * 0.75;
    if (property.bedrooms !== null) {
        if (property.bedrooms > Math.ceil(roomsNeeded) && incomePct < 1.0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.fewer_bedrooms_recommended"),
            });
        }
        else if (householdPct < 1.0 && householdPct > 0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.household_high", {
                    bedrooms: String(property.bedrooms),
                }),
            });
        }
        else if (householdPct <= 0) {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.household_too_large", {
                    bedrooms: String(property.bedrooms),
                }),
            });
        }
    }
    // Date warnings
    if (property.availableDate) {
        if (user.moveInDateType === "immediately" && datePct < 1.0 && datePct > 0) {
            warnings.push({
                type: "orange",
                message: getLocalizedText("rentmatch.score_warning.move_in_not_available"),
            });
        }
        else if (datePct === 0 &&
            user.moveInDateType !== "" &&
            user.moveInDateType !== "flexible") {
            warnings.push({
                type: "red",
                message: getLocalizedText("rentmatch.score_warning.move_in_too_early"),
            });
        }
    }
    return warnings;
}
// ── Main scoring function ─────────────────────────────────────────────
export function calculateRentMatchScore(user, property) {
    var incomePct = calcIncomePct(user, property);
    var householdPct = calcHouseholdPct(user, property, incomePct);
    var datePct = calcDatePct(user, property);
    var warnings = calcWarnings(user, property, incomePct, householdPct, datePct);
    // Additive: each category contributes weight × pct
    // Household and date CAN go negative, subtracting from income's score
    // Income stays 0–1 (no negative income)
    var rawScore = incomePct * WEIGHTS.income +
        householdPct * WEIGHTS.household +
        datePct * WEIGHTS.date;
    // Income gate: below minRatio, further scale down
    var totalIncome = user.netIncome + user.otherIncome;
    var finalScore = rawScore;
    if (totalIncome > 0) {
        var ratio = totalIncome / property.rent;
        var minRatio = getIncomeThresholds(property.rent, user.adults, user.minors).minRatio;
        if (ratio < minRatio) {
            // Only scale positive scores. Multiplying a negative score by a fraction < 1
            // artificially shrinks it towards 0, making a terrible match look 'better'.
            if (rawScore > 0) {
                finalScore = rawScore * (ratio / minRatio);
            }
        }
    }
    // Asymptotic curve to ensure the score keeps dropping but never reaches 0 (or goes negative)
    var displayScore = finalScore;
    if (finalScore < 15) {
        displayScore = 15 * Math.exp((finalScore - 15) / 25);
    }
    return {
        score: Math.max(1, Math.min(100, Math.round(displayScore))),
        warnings: warnings,
        incomePct: incomePct,
        householdPct: householdPct,
        datePct: datePct,
    };
}
